import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import LogoLight from 'public/images/logo-light.svg?react'
import LogoDark from 'public/images/logo-dark.svg?react'
import LogoIcon from 'public/images/logo-icon.svg?react'

import { ChevronLeft, ChevronRight, Search, X } from 'lucide-react'

import { useTheme } from '@/components/theme-provider'
import { Button } from '@/components/ui/button'
import SearchBar from '../../../components/SearchBar'
import { usePageInfo } from '../../../config/queries'
import { useActionable, useBreakpoint } from '../../../helpers'
import GameSwitcher from './GameSwitcher'
import HeaderDropdown from './HeaderDropdown'
import TeamNavigationMenu from './TeamNavigationMenu'
import EventNavigationMenu from './EventNavigationMenu'
import LoginButtons from '../../../components/LoginButtons'

const Header = () => {
  const {
    data: { user },
  } = usePageInfo()
  const [searching, setSearching] = useState(false)
  const breakpoint = useBreakpoint()
  const { t } = useTranslation()
  const { currentScheme } = useTheme()
  const {
    currentAction,
    hasNextAction,
    actionableEntities,
    hasPreviousAction,
    currentActionIndex,
    setCurrentActionIndex,
    dismissActionableEntity,
  } = useActionable(user)

  const Logo = currentScheme === 'dark' ? LogoLight : LogoDark

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b bg-card ${
        currentAction ? 'mb-8' : ''
      }`}
    >
      <div className='root-width max-sm:px-4 flex h-14 items-center justify-between'>
        <div className='flex items-center gap-0.5 justify-between'>
          <div className='flex'>
            <Link to='/' className='mr-2'>
              <LogoIcon
                className='w-10 h-10 hidden max-[420px]:block'
                color='var(--color-primary)'
              />
              <Logo
                className='hidden min-[421px]:block h-12 w-[10.7rem]'
                color='var(--color-primary)'
              />
            </Link>
          </div>

          <div className='mr-4 lg:mr-12 lg:ml-4'>
            <GameSwitcher className='hidden lg:flex' />
          </div>

          <TeamNavigationMenu />
          <EventNavigationMenu />
        </div>
        <div>
          <div className='sm:ml-4 flex items-center'>
            <Search
              size={18}
              className='mr-2 sm:mr-4 cursor-pointer p-2 w-[2.1rem] h-[2.1rem] min-w-[2.1rem] min-h-[2.1rem]'
              onClick={() => setSearching(true)}
            />

            {searching && (
              <SearchBar
                type='dialog'
                placeholder={t(
                  breakpoint === 'xs'
                    ? 'shared.search'
                    : 'header.search-placeholder',
                )}
                setOpen={setSearching}
              />
            )}

            {user ? <HeaderDropdown /> : <LoginButtons />}
          </div>
        </div>
      </div>

      {currentAction ? (
        <div className={`absolute top-14 w-screen ${currentAction.className}`}>
          <div className='flex items-center justify-between container gap-1 h-8'>
            <span className='dark:text-muted text-sm font-bold truncate'>
              {currentAction.text}
            </span>
            <div className='flex items-center gap-2 [&>svg]:text-background'>
              {currentAction.ctaPath ? (
                <Link to={currentAction.ctaPath}>
                  <Button variant='outline' size='xs'>
                    {currentAction.ctaText}
                  </Button>
                </Link>
              ) : null}

              {actionableEntities.length > 1 ? (
                <>
                  <ChevronLeft
                    size={14}
                    className={
                      hasPreviousAction
                        ? 'cursor-pointer'
                        : 'opacity-30 pointer-events-none'
                    }
                    onClick={() =>
                      hasPreviousAction
                        ? setCurrentActionIndex(currentActionIndex - 1)
                        : null
                    }
                  />
                  <ChevronRight
                    size={14}
                    className={
                      hasNextAction
                        ? 'cursor-pointer'
                        : 'opacity-30 pointer-events-none'
                    }
                    onClick={() =>
                      hasNextAction
                        ? setCurrentActionIndex(currentActionIndex + 1)
                        : null
                    }
                  />
                </>
              ) : null}

              <X
                size={14}
                className='cursor-pointer'
                onClick={() => dismissActionableEntity(currentAction)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </header>
  )
}

export default Header
